/**
 * 🎯 MEGA Icon Registry - 400+ Professional Icons
 * EXTENDED version with all modern technologies
 */
import * as Si from 'react-icons/si';
import * as Fa from 'react-icons/fa';
import * as Md from 'react-icons/md';
import * as Bs from 'react-icons/bs';
import * as Ai from 'react-icons/ai';
import * as Di from 'react-icons/di';
import * as Tb from 'react-icons/tb';

export interface IconMeta {
  key: string;
  name: string;
  color: string;
  category: string;
}

export interface ResolvedIcon extends IconMeta {
  IconComponent: any;
  hasComponent: boolean;
}

// 🎨 MEGA REGISTRY - 400+ icons organized by categories
const RAW_CATEGORIES: Record<string, IconMeta[]> = {
  'Frontend Frameworks': [
    { key: 'react', name: 'React', color: '#61dafb', category: 'Frontend Frameworks' },
    { key: 'vue', name: 'Vue.js', color: '#4fc08d', category: 'Frontend Frameworks' },
    { key: 'angular', name: 'Angular', color: '#dd1b16', category: 'Frontend Frameworks' },
    { key: 'svelte', name: 'Svelte', color: '#ff3e00', category: 'Frontend Frameworks' },
    { key: 'nextjs', name: 'Next.js', color: '#000000', category: 'Frontend Frameworks' },
    { key: 'nuxtjs', name: 'Nuxt.js', color: '#00dc82', category: 'Frontend Frameworks' },
    { key: 'gatsby', name: 'Gatsby', color: '#663399', category: 'Frontend Frameworks' },
    { key: 'astro', name: 'Astro', color: '#ff5d01', category: 'Frontend Frameworks' },
    { key: 'remix', name: 'Remix', color: '#000000', category: 'Frontend Frameworks' },
    { key: 'qwik', name: 'Qwik', color: '#ac7ef4', category: 'Frontend Frameworks' },
    { key: 'solid', name: 'SolidJS', color: '#2c4f7c', category: 'Frontend Frameworks' },
    { key: 'preact', name: 'Preact', color: '#673ab8', category: 'Frontend Frameworks' },
    { key: 'ember', name: 'Ember.js', color: '#e04e39', category: 'Frontend Frameworks' },
    { key: 'backbone', name: 'Backbone.js', color: '#0071b5', category: 'Frontend Frameworks' },
    { key: 'meteor', name: 'Meteor', color: '#de4f4f', category: 'Frontend Frameworks' },
    { key: 'alpine', name: 'Alpine.js', color: '#8bc34a', category: 'Frontend Frameworks' },
    { key: 'lit', name: 'Lit', color: '#324fff', category: 'Frontend Frameworks' },
    { key: 'stencil', name: 'Stencil', color: '#16161d', category: 'Frontend Frameworks' },
    { key: 'mithril', name: 'Mithril', color: '#1e88e5', category: 'Frontend Frameworks' },
    { key: 'inferno', name: 'Inferno', color: '#e91e63', category: 'Frontend Frameworks' }
  ],

  'Programming Languages': [
    { key: 'javascript', name: 'JavaScript', color: '#f7df1e', category: 'Programming Languages' },
    { key: 'typescript', name: 'TypeScript', color: '#3178c6', category: 'Programming Languages' },
    { key: 'python', name: 'Python', color: '#3776ab', category: 'Programming Languages' },
    { key: 'java', name: 'Java', color: '#ed8b00', category: 'Programming Languages' },
    { key: 'csharp', name: 'C#', color: '#239120', category: 'Programming Languages' },
    { key: 'cpp', name: 'C++', color: '#00599c', category: 'Programming Languages' },
    { key: 'c', name: 'C', color: '#a8b9cc', category: 'Programming Languages' },
    { key: 'go', name: 'Go', color: '#00add8', category: 'Programming Languages' },
    { key: 'rust', name: 'Rust', color: '#000000', category: 'Programming Languages' },
    { key: 'php', name: 'PHP', color: '#777bb4', category: 'Programming Languages' },
    { key: 'ruby', name: 'Ruby', color: '#cc342d', category: 'Programming Languages' },
    { key: 'swift', name: 'Swift', color: '#fa7343', category: 'Programming Languages' },
    { key: 'kotlin', name: 'Kotlin', color: '#7f52ff', category: 'Programming Languages' },
    { key: 'dart', name: 'Dart', color: '#0175c2', category: 'Programming Languages' },
    { key: 'scala', name: 'Scala', color: '#dc322f', category: 'Programming Languages' },
    { key: 'elixir', name: 'Elixir', color: '#4b275f', category: 'Programming Languages' },
    { key: 'clojure', name: 'Clojure', color: '#5881d8', category: 'Programming Languages' },
    { key: 'haskell', name: 'Haskell', color: '#5d4f85', category: 'Programming Languages' },
    { key: 'r', name: 'R', color: '#276dc3', category: 'Programming Languages' },
    { key: 'matlab', name: 'MATLAB', color: '#0076a8', category: 'Programming Languages' },
    { key: 'perl', name: 'Perl', color: '#39457e', category: 'Programming Languages' },
    { key: 'lua', name: 'Lua', color: '#2c2d72', category: 'Programming Languages' },
    { key: 'julia', name: 'Julia', color: '#9558b2', category: 'Programming Languages' },
    { key: 'ocaml', name: 'OCaml', color: '#ec6813', category: 'Programming Languages' },
    { key: 'erlang', name: 'Erlang', color: '#a90533', category: 'Programming Languages' },
    { key: 'fsharp', name: 'F#', color: '#378bba', category: 'Programming Languages' },
    { key: 'groovy', name: 'Groovy', color: '#4298b8', category: 'Programming Languages' },
    { key: 'crystal', name: 'Crystal', color: '#000000', category: 'Programming Languages' },
    { key: 'nim', name: 'Nim', color: '#ffe953', category: 'Programming Languages' },
    { key: 'zig', name: 'Zig', color: '#ec915c', category: 'Programming Languages' }
  ],

  'Styling & UI': [
    { key: 'html5', name: 'HTML5', color: '#e34f26', category: 'Styling & UI' },
    { key: 'css3', name: 'CSS3', color: '#1572b6', category: 'Styling & UI' },
    { key: 'sass', name: 'Sass', color: '#cf649a', category: 'Styling & UI' },
    { key: 'less', name: 'Less', color: '#1d365d', category: 'Styling & UI' },
    { key: 'stylus', name: 'Stylus', color: '#ff6347', category: 'Styling & UI' },
    { key: 'tailwindcss', name: 'Tailwind CSS', color: '#06b6d4', category: 'Styling & UI' },
    { key: 'bootstrap', name: 'Bootstrap', color: '#7952b3', category: 'Styling & UI' },
    { key: 'bulma', name: 'Bulma', color: '#00d1b2', category: 'Styling & UI' },
    { key: 'materialui', name: 'Material-UI', color: '#0081cb', category: 'Styling & UI' },
    { key: 'antdesign', name: 'Ant Design', color: '#1890ff', category: 'Styling & UI' },
    { key: 'chakraui', name: 'Chakra UI', color: '#319795', category: 'Styling & UI' },
    { key: 'styledcomponents', name: 'Styled Components', color: '#db7093', category: 'Styling & UI' },
    { key: 'emotion', name: 'Emotion', color: '#d36ac2', category: 'Styling & UI' },
    { key: 'figma', name: 'Figma', color: '#f24e1e', category: 'Styling & UI' },
    { key: 'sketch', name: 'Sketch', color: '#f7b500', category: 'Styling & UI' },
    { key: 'framer', name: 'Framer', color: '#0055ff', category: 'Styling & UI' },
    { key: 'adobe-xd', name: 'Adobe XD', color: '#ff61f6', category: 'Styling & UI' },
    { key: 'invision', name: 'InVision', color: '#ff3366', category: 'Styling & UI' },
    { key: 'zeplin', name: 'Zeplin', color: '#fdbc2a', category: 'Styling & UI' },
    { key: 'principle', name: 'Principle', color: '#000000', category: 'Styling & UI' },
    { key: 'foundation', name: 'Foundation', color: '#008cba', category: 'Styling & UI' },
    { key: 'semantic-ui', name: 'Semantic UI', color: '#35bdb2', category: 'Styling & UI' },
    { key: 'uikit', name: 'UIKit', color: '#2396f3', category: 'Styling & UI' },
    { key: 'windicss', name: 'WindiCSS', color: '#48b0f1', category: 'Styling & UI' },
    { key: 'postcss', name: 'PostCSS', color: '#dd3a0a', category: 'Styling & UI' }
  ],

  'Backend Frameworks': [
    { key: 'nodejs', name: 'Node.js', color: '#339933', category: 'Backend Frameworks' },
    { key: 'deno', name: 'Deno', color: '#000000', category: 'Backend Frameworks' },
    { key: 'bun', name: 'Bun', color: '#000000', category: 'Backend Frameworks' },
    { key: 'express', name: 'Express.js', color: '#000000', category: 'Backend Frameworks' },
    { key: 'fastify', name: 'Fastify', color: '#000000', category: 'Backend Frameworks' },
    { key: 'nestjs', name: 'NestJS', color: '#e0234e', category: 'Backend Frameworks' },
    { key: 'koa', name: 'Koa.js', color: '#33333d', category: 'Backend Frameworks' },
    { key: 'laravel', name: 'Laravel', color: '#ff2d20', category: 'Backend Frameworks' },
    { key: 'django', name: 'Django', color: '#092e20', category: 'Backend Frameworks' },
    { key: 'flask', name: 'Flask', color: '#000000', category: 'Backend Frameworks' },
    { key: 'fastapi', name: 'FastAPI', color: '#009688', category: 'Backend Frameworks' },
    { key: 'spring', name: 'Spring Boot', color: '#6db33f', category: 'Backend Frameworks' },
    { key: 'dotnet', name: '.NET Core', color: '#512bd4', category: 'Backend Frameworks' },
    { key: 'rails', name: 'Ruby on Rails', color: '#cc0000', category: 'Backend Frameworks' },
    { key: 'gin', name: 'Gin (Go)', color: '#00add8', category: 'Backend Frameworks' },
    { key: 'fiber', name: 'Fiber (Go)', color: '#00d9ff', category: 'Backend Frameworks' },
    { key: 'actix', name: 'Actix Web', color: '#000000', category: 'Backend Frameworks' },
    { key: 'rocket', name: 'Rocket (Rust)', color: '#d33847', category: 'Backend Frameworks' },
    { key: 'phoenix', name: 'Phoenix', color: '#fd4f00', category: 'Backend Frameworks' },
    { key: 'sinatra', name: 'Sinatra', color: '#000000', category: 'Backend Frameworks' },
    { key: 'tornado', name: 'Tornado', color: '#0099cc', category: 'Backend Frameworks' },
    { key: 'aiohttp', name: 'aiohttp', color: '#2c5aa0', category: 'Backend Frameworks' },
    { key: 'hapi', name: 'Hapi.js', color: '#a97c50', category: 'Backend Frameworks' },
    { key: 'sails', name: 'Sails.js', color: '#14acc2', category: 'Backend Frameworks' },
    { key: 'adonis', name: 'AdonisJS', color: '#220052', category: 'Backend Frameworks' }
  ],

  'Databases': [
    { key: 'postgresql', name: 'PostgreSQL', color: '#336791', category: 'Databases' },
    { key: 'mysql', name: 'MySQL', color: '#4479a1', category: 'Databases' },
    { key: 'mongodb', name: 'MongoDB', color: '#47a248', category: 'Databases' },
    { key: 'redis', name: 'Redis', color: '#dc382d', category: 'Databases' },
    { key: 'sqlite', name: 'SQLite', color: '#003b57', category: 'Databases' },
    { key: 'mariadb', name: 'MariaDB', color: '#003545', category: 'Databases' },
    { key: 'supabase', name: 'Supabase', color: '#3ecf8e', category: 'Databases' },
    { key: 'firebase', name: 'Firebase', color: '#ffca28', category: 'Databases' },
    { key: 'cassandra', name: 'Cassandra', color: '#1287b1', category: 'Databases' },
    { key: 'elasticsearch', name: 'Elasticsearch', color: '#005571', category: 'Databases' },
    { key: 'neo4j', name: 'Neo4j', color: '#008cc1', category: 'Databases' },
    { key: 'dynamodb', name: 'DynamoDB', color: '#3294da', category: 'Databases' },
    { key: 'couchdb', name: 'CouchDB', color: '#e42528', category: 'Databases' },
    { key: 'influxdb', name: 'InfluxDB', color: '#22adf6', category: 'Databases' },
    { key: 'timescaledb', name: 'TimescaleDB', color: '#fdb515', category: 'Databases' },
    { key: 'cockroachdb', name: 'CockroachDB', color: '#6933ff', category: 'Databases' },
    { key: 'planetscale', name: 'PlanetScale', color: '#000000', category: 'Databases' },
    { key: 'fauna', name: 'FaunaDB', color: '#3a1ab0', category: 'Databases' },
    { key: 'arangodb', name: 'ArangoDB', color: '#dde072', category: 'Databases' },
    { key: 'rethinkdb', name: 'RethinkDB', color: '#f6c915', category: 'Databases' }
  ],

  'Cloud & DevOps': [
    { key: 'docker', name: 'Docker', color: '#2496ed', category: 'Cloud & DevOps' },
    { key: 'kubernetes', name: 'Kubernetes', color: '#326ce5', category: 'Cloud & DevOps' },
    { key: 'jenkins', name: 'Jenkins', color: '#d24939', category: 'Cloud & DevOps' },
    { key: 'terraform', name: 'Terraform', color: '#7b42bc', category: 'Cloud & DevOps' },
    { key: 'ansible', name: 'Ansible', color: '#ee0000', category: 'Cloud & DevOps' },
    { key: 'nginx', name: 'Nginx', color: '#009639', category: 'Cloud & DevOps' },
    { key: 'aws', name: 'Amazon AWS', color: '#ff9900', category: 'Cloud & DevOps' },
    { key: 'gcp', name: 'Google Cloud', color: '#4285f4', category: 'Cloud & DevOps' },
    { key: 'azure', name: 'Microsoft Azure', color: '#0078d4', category: 'Cloud & DevOps' },
    { key: 'vercel', name: 'Vercel', color: '#000000', category: 'Cloud & DevOps' },
    { key: 'netlify', name: 'Netlify', color: '#00c7b7', category: 'Cloud & DevOps' },
    { key: 'heroku', name: 'Heroku', color: '#430098', category: 'Cloud & DevOps' },
    { key: 'digitalocean', name: 'DigitalOcean', color: '#0080ff', category: 'Cloud & DevOps' },
    { key: 'linode', name: 'Linode', color: '#00a95c', category: 'Cloud & DevOps' },
    { key: 'vultr', name: 'Vultr', color: '#007bfc', category: 'Cloud & DevOps' },
    { key: 'cloudflare', name: 'Cloudflare', color: '#f38020', category: 'Cloud & DevOps' },
    { key: 'railway', name: 'Railway', color: '#0b0d0e', category: 'Cloud & DevOps' },
    { key: 'render', name: 'Render', color: '#46e3b7', category: 'Cloud & DevOps' },
    { key: 'fly', name: 'Fly.io', color: '#8b5cf6', category: 'Cloud & DevOps' },
    { key: 'supabase-edge', name: 'Supabase Edge', color: '#3ecf8e', category: 'Cloud & DevOps' }
  ],

  'Development Tools': [
    { key: 'vscode', name: 'VS Code', color: '#007acc', category: 'Development Tools' },
    { key: 'webstorm', name: 'WebStorm', color: '#000000', category: 'Development Tools' },
    { key: 'intellij', name: 'IntelliJ IDEA', color: '#000000', category: 'Development Tools' },
    { key: 'pycharm', name: 'PyCharm', color: '#000000', category: 'Development Tools' },
    { key: 'androidstudio', name: 'Android Studio', color: '#3ddc84', category: 'Development Tools' },
    { key: 'xcode', name: 'Xcode', color: '#1575f9', category: 'Development Tools' },
    { key: 'vim', name: 'Vim', color: '#019833', category: 'Development Tools' },
    { key: 'neovim', name: 'Neovim', color: '#57a143', category: 'Development Tools' },
    { key: 'emacs', name: 'Emacs', color: '#7f5ab6', category: 'Development Tools' },
    { key: 'sublime', name: 'Sublime Text', color: '#ff9800', category: 'Development Tools' },
    { key: 'atom', name: 'Atom', color: '#66595c', category: 'Development Tools' },
    { key: 'bracket', name: 'Brackets', color: '#6cc04a', category: 'Development Tools' },
    { key: 'rider', name: 'Rider', color: '#000000', category: 'Development Tools' },
    { key: 'eclipse', name: 'Eclipse', color: '#2c2255', category: 'Development Tools' },
    { key: 'netbeans', name: 'NetBeans', color: '#1b6ec8', category: 'Development Tools' },
    { key: 'codepen', name: 'CodePen', color: '#000000', category: 'Development Tools' },
    { key: 'codesandbox', name: 'CodeSandbox', color: '#000000', category: 'Development Tools' },
    { key: 'replit', name: 'Replit', color: '#667881', category: 'Development Tools' },
    { key: 'gitpod', name: 'Gitpod', color: '#ffb45b', category: 'Development Tools' },
    { key: 'stackblitz', name: 'StackBlitz', color: '#1389fd', category: 'Development Tools' }
  ],

  'Build Tools': [
    { key: 'webpack', name: 'Webpack', color: '#8dd6f9', category: 'Build Tools' },
    { key: 'vite', name: 'Vite', color: '#646cff', category: 'Build Tools' },
    { key: 'rollup', name: 'Rollup', color: '#ec3a37', category: 'Build Tools' },
    { key: 'parcel', name: 'Parcel', color: '#e6a54d', category: 'Build Tools' },
    { key: 'babel', name: 'Babel', color: '#f9dc3e', category: 'Build Tools' },
    { key: 'eslint', name: 'ESLint', color: '#4b32c3', category: 'Build Tools' },
    { key: 'prettier', name: 'Prettier', color: '#f7b93e', category: 'Build Tools' },
    { key: 'gulp', name: 'Gulp', color: '#da4648', category: 'Build Tools' },
    { key: 'grunt', name: 'Grunt', color: '#fba919', category: 'Build Tools' },
    { key: 'turbo', name: 'Turbo', color: '#ef4444', category: 'Build Tools' },
    { key: 'esbuild', name: 'esbuild', color: '#ffcf00', category: 'Build Tools' },
    { key: 'swc', name: 'SWC', color: '#ffa500', category: 'Build Tools' },
    { key: 'rome', name: 'Rome', color: '#ffc905', category: 'Build Tools' },
    { key: 'biome', name: 'Biome', color: '#60a5fa', category: 'Build Tools' },
    { key: 'vitest', name: 'Vitest', color: '#6e9f18', category: 'Build Tools' }
  ],

  'Version Control': [
    { key: 'git', name: 'Git', color: '#f05032', category: 'Version Control' },
    { key: 'github', name: 'GitHub', color: '#181717', category: 'Version Control' },
    { key: 'gitlab', name: 'GitLab', color: '#fc6d26', category: 'Version Control' },
    { key: 'bitbucket', name: 'Bitbucket', color: '#0052cc', category: 'Version Control' },
    { key: 'azure-devops', name: 'Azure DevOps', color: '#0078d4', category: 'Version Control' },
    { key: 'sourcetree', name: 'Sourcetree', color: '#0052cc', category: 'Version Control' },
    { key: 'gitkraken', name: 'GitKraken', color: '#179287', category: 'Version Control' },
    { key: 'tower', name: 'Tower', color: '#e94b3c', category: 'Version Control' },
    { key: 'fork', name: 'Fork', color: '#000000', category: 'Version Control' },
    { key: 'svn', name: 'Subversion', color: '#809cc9', category: 'Version Control' }
  ],

  'Mobile Development': [
    { key: 'reactnative', name: 'React Native', color: '#61dafb', category: 'Mobile Development' },
    { key: 'flutter', name: 'Flutter', color: '#02569b', category: 'Mobile Development' },
    { key: 'ionic', name: 'Ionic', color: '#3880ff', category: 'Mobile Development' },
    { key: 'xamarin', name: 'Xamarin', color: '#3498db', category: 'Mobile Development' },
    { key: 'cordova', name: 'Apache Cordova', color: '#e8e8e8', category: 'Mobile Development' },
    { key: 'phonegap', name: 'PhoneGap', color: '#00adef', category: 'Mobile Development' },
    { key: 'nativescript', name: 'NativeScript', color: '#3655ff', category: 'Mobile Development' },
    { key: 'expo', name: 'Expo', color: '#000020', category: 'Mobile Development' },
    { key: 'capacitor', name: 'Capacitor', color: '#119eff', category: 'Mobile Development' },
    { key: 'quasar', name: 'Quasar', color: '#1976d2', category: 'Mobile Development' }
  ],

  'Testing Frameworks': [
    { key: 'jest', name: 'Jest', color: '#c21325', category: 'Testing Frameworks' },
    { key: 'vitest', name: 'Vitest', color: '#6e9f18', category: 'Testing Frameworks' },
    { key: 'cypress', name: 'Cypress', color: '#17202c', category: 'Testing Frameworks' },
    { key: 'playwright', name: 'Playwright', color: '#2eac4a', category: 'Testing Frameworks' },
    { key: 'selenium', name: 'Selenium', color: '#43b02a', category: 'Testing Frameworks' },
    { key: 'puppeteer', name: 'Puppeteer', color: '#40b883', category: 'Testing Frameworks' },
    { key: 'testing-library', name: 'Testing Library', color: '#e33332', category: 'Testing Frameworks' },
    { key: 'mocha', name: 'Mocha', color: '#8d6748', category: 'Testing Frameworks' },
    { key: 'jasmine', name: 'Jasmine', color: '#8a4182', category: 'Testing Frameworks' },
    { key: 'karma', name: 'Karma', color: '#56c5a8', category: 'Testing Frameworks' },
    { key: 'storybook', name: 'Storybook', color: '#ff4785', category: 'Testing Frameworks' },
    { key: 'chromatic', name: 'Chromatic', color: '#fc521f', category: 'Testing Frameworks' }
  ],

  'Data Science & AI': [
    { key: 'tensorflow', name: 'TensorFlow', color: '#ff6f00', category: 'Data Science & AI' },
    { key: 'pytorch', name: 'PyTorch', color: '#ee4c2c', category: 'Data Science & AI' },
    { key: 'pandas', name: 'Pandas', color: '#150458', category: 'Data Science & AI' },
    { key: 'numpy', name: 'NumPy', color: '#013243', category: 'Data Science & AI' },
    { key: 'scikit-learn', name: 'Scikit-learn', color: '#f7931e', category: 'Data Science & AI' },
    { key: 'jupyter', name: 'Jupyter', color: '#f37626', category: 'Data Science & AI' },
    { key: 'anaconda', name: 'Anaconda', color: '#44a833', category: 'Data Science & AI' },
    { key: 'opencv', name: 'OpenCV', color: '#5c3ee8', category: 'Data Science & AI' },
    { key: 'keras', name: 'Keras', color: '#d00000', category: 'Data Science & AI' },
    { key: 'plotly', name: 'Plotly', color: '#3f4f75', category: 'Data Science & AI' },
    { key: 'tableau', name: 'Tableau', color: '#e97627', category: 'Data Science & AI' },
    { key: 'powerbi', name: 'Power BI', color: '#f2c811', category: 'Data Science & AI' },
    { key: 'apache-spark', name: 'Apache Spark', color: '#e25a1c', category: 'Data Science & AI' },
    { key: 'databricks', name: 'Databricks', color: '#ff3621', category: 'Data Science & AI' },
    { key: 'snowflake', name: 'Snowflake', color: '#29b5e8', category: 'Data Science & AI' }
  ],

  'CMS & E-commerce': [
    { key: 'wordpress', name: 'WordPress', color: '#21759b', category: 'CMS & E-commerce' },
    { key: 'drupal', name: 'Drupal', color: '#0678be', category: 'CMS & E-commerce' },
    { key: 'joomla', name: 'Joomla', color: '#5091cd', category: 'CMS & E-commerce' },
    { key: 'contentful', name: 'Contentful', color: '#2478cc', category: 'CMS & E-commerce' },
    { key: 'strapi', name: 'Strapi', color: '#2e7eea', category: 'CMS & E-commerce' },
    { key: 'sanity', name: 'Sanity', color: '#f03e2f', category: 'CMS & E-commerce' },
    { key: 'ghost', name: 'Ghost', color: '#15171a', category: 'CMS & E-commerce' },
    { key: 'shopify', name: 'Shopify', color: '#7ab55c', category: 'CMS & E-commerce' },
    { key: 'woocommerce', name: 'WooCommerce', color: '#96588a', category: 'CMS & E-commerce' },
    { key: 'magento', name: 'Magento', color: '#ee672f', category: 'CMS & E-commerce' },
    { key: 'bigcommerce', name: 'BigCommerce', color: '#121118', category: 'CMS & E-commerce' },
    { key: 'prestashop', name: 'PrestaShop', color: '#df0067', category: 'CMS & E-commerce' }
  ],

  'Game Development': [
    { key: 'unity', name: 'Unity', color: '#000000', category: 'Game Development' },
    { key: 'unreal', name: 'Unreal Engine', color: '#0e1128', category: 'Game Development' },
    { key: 'godot', name: 'Godot', color: '#478cbf', category: 'Game Development' },
    { key: 'construct', name: 'Construct 3', color: '#00b56a', category: 'Game Development' },
    { key: 'gamemaker', name: 'GameMaker', color: '#8cc04b', category: 'Game Development' },
    { key: 'defold', name: 'Defold', color: '#1fb6d3', category: 'Game Development' },
    { key: 'cocos2d', name: 'Cocos2D', color: '#55c2e1', category: 'Game Development' },
    { key: 'love2d', name: 'LÖVE', color: '#ea316e', category: 'Game Development' },
    { key: 'phaser', name: 'Phaser', color: '#0c7a9e', category: 'Game Development' },
    { key: 'threejs', name: 'Three.js', color: '#000000', category: 'Game Development' }
  ],

  'Blockchain & Web3': [
    { key: 'ethereum', name: 'Ethereum', color: '#627eea', category: 'Blockchain & Web3' },
    { key: 'bitcoin', name: 'Bitcoin', color: '#f7931a', category: 'Blockchain & Web3' },
    { key: 'solana', name: 'Solana', color: '#9945ff', category: 'Blockchain & Web3' },
    { key: 'polygon', name: 'Polygon', color: '#8247e5', category: 'Blockchain & Web3' },
    { key: 'chainlink', name: 'Chainlink', color: '#375bd2', category: 'Blockchain & Web3' },
    { key: 'web3js', name: 'Web3.js', color: '#f16822', category: 'Blockchain & Web3' },
    { key: 'ethers', name: 'Ethers.js', color: '#29b6d6', category: 'Blockchain & Web3' },
    { key: 'metamask', name: 'MetaMask', color: '#f6851b', category: 'Blockchain & Web3' },
    { key: 'truffle', name: 'Truffle', color: '#5e464d', category: 'Blockchain & Web3' },
    { key: 'hardhat', name: 'Hardhat', color: '#fff04d', category: 'Blockchain & Web3' }
  ],

  'APIs & Integration': [
    { key: 'graphql', name: 'GraphQL', color: '#e10098', category: 'APIs & Integration' },
    { key: 'rest', name: 'REST API', color: '#ff6b35', category: 'APIs & Integration' },
    { key: 'grpc', name: 'gRPC', color: '#4285f4', category: 'APIs & Integration' },
    { key: 'apollo', name: 'Apollo GraphQL', color: '#311c87', category: 'APIs & Integration' },
    { key: 'postman', name: 'Postman', color: '#ff6c37', category: 'APIs & Integration' },
    { key: 'insomnia', name: 'Insomnia', color: '#4000bf', category: 'APIs & Integration' },
    { key: 'swagger', name: 'Swagger/OpenAPI', color: '#85ea2d', category: 'APIs & Integration' },
    { key: 'webhook', name: 'Webhooks', color: '#00d4aa', category: 'APIs & Integration' },
    { key: 'zapier', name: 'Zapier', color: '#ff4a00', category: 'APIs & Integration' },
    { key: 'ifttt', name: 'IFTTT', color: '#33ccff', category: 'APIs & Integration' }
  ],

  'Security & DevSecOps': [
    { key: 'oauth', name: 'OAuth', color: '#3c4043', category: 'Security & DevSecOps' },
    { key: 'jwt', name: 'JSON Web Tokens', color: '#000000', category: 'Security & DevSecOps' },
    { key: 'auth0', name: 'Auth0', color: '#eb5424', category: 'Security & DevSecOps' },
    { key: 'okta', name: 'Okta', color: '#007dc1', category: 'Security & DevSecOps' },
    { key: 'keycloak', name: 'Keycloak', color: '#4d9221', category: 'Security & DevSecOps' },
    { key: 'vault', name: 'HashiCorp Vault', color: '#000000', category: 'Security & DevSecOps' },
    { key: 'sonarqube', name: 'SonarQube', color: '#4e9bcd', category: 'Security & DevSecOps' },
    { key: 'snyk', name: 'Snyk', color: '#4c4a73', category: 'Security & DevSecOps' },
    { key: 'owasp', name: 'OWASP', color: '#000000', category: 'Security & DevSecOps' },
    { key: 'letsencrypt', name: "Let's Encrypt", color: '#003a70', category: 'Security & DevSecOps' }
  ],

  'Monitoring & Analytics': [
    { key: 'prometheus', name: 'Prometheus', color: '#e6522c', category: 'Monitoring & Analytics' },
    { key: 'grafana', name: 'Grafana', color: '#f46800', category: 'Monitoring & Analytics' },
    { key: 'datadog', name: 'Datadog', color: '#632ca6', category: 'Monitoring & Analytics' },
    { key: 'newrelic', name: 'New Relic', color: '#008c99', category: 'Monitoring & Analytics' },
    { key: 'sentry', name: 'Sentry', color: '#362d59', category: 'Monitoring & Analytics' },
    { key: 'bugsnag', name: 'Bugsnag', color: '#4949e7', category: 'Monitoring & Analytics' },
    { key: 'rollbar', name: 'Rollbar', color: '#3c78d8', category: 'Monitoring & Analytics' },
    { key: 'hotjar', name: 'Hotjar', color: '#fd3a5c', category: 'Monitoring & Analytics' },
    { key: 'googleanalytics', name: 'Google Analytics', color: '#e37400', category: 'Monitoring & Analytics' },
    { key: 'mixpanel', name: 'Mixpanel', color: '#7856ff', category: 'Monitoring & Analytics' }
  ],

  'Operating Systems': [
    { key: 'windows', name: 'Windows', color: '#0078d4', category: 'Operating Systems' },
    { key: 'macos', name: 'macOS', color: '#000000', category: 'Operating Systems' },
    { key: 'linux', name: 'Linux', color: '#fcc624', category: 'Operating Systems' },
    { key: 'ubuntu', name: 'Ubuntu', color: '#e95420', category: 'Operating Systems' },
    { key: 'debian', name: 'Debian', color: '#a81d33', category: 'Operating Systems' },
    { key: 'centos', name: 'CentOS', color: '#262577', category: 'Operating Systems' },
    { key: 'redhat', name: 'Red Hat', color: '#ee0000', category: 'Operating Systems' },
    { key: 'fedora', name: 'Fedora', color: '#294172', category: 'Operating Systems' },
    { key: 'arch', name: 'Arch Linux', color: '#1793d1', category: 'Operating Systems' },
    { key: 'opensuse', name: 'openSUSE', color: '#73ba25', category: 'Operating Systems' },
    { key: 'freebsd', name: 'FreeBSD', color: '#ab2b28', category: 'Operating Systems' },
    { key: 'alpine-linux', name: 'Alpine Linux', color: '#0d597f', category: 'Operating Systems' }
  ],

  'Networking & Protocols': [
    { key: 'http', name: 'HTTP', color: '#005a9c', category: 'Networking & Protocols' },
    { key: 'https', name: 'HTTPS', color: '#0f7b0f', category: 'Networking & Protocols' },
    { key: 'tcp', name: 'TCP/IP', color: '#1e88e5', category: 'Networking & Protocols' },
    { key: 'websocket', name: 'WebSocket', color: '#010101', category: 'Networking & Protocols' },
    { key: 'ssh', name: 'SSH', color: '#000000', category: 'Networking & Protocols' },
    { key: 'ftp', name: 'FTP', color: '#0066cc', category: 'Networking & Protocols' },
    { key: 'smtp', name: 'SMTP', color: '#cc0000', category: 'Networking & Protocols' },
    { key: 'dns', name: 'DNS', color: '#326ce5', category: 'Networking & Protocols' },
    { key: 'vpn', name: 'VPN', color: '#ff6b6b', category: 'Networking & Protocols' },
    { key: 'proxy', name: 'Proxy', color: '#4ecdc4', category: 'Networking & Protocols' }
  ],

  'File Formats & Standards': [
    { key: 'json', name: 'JSON', color: '#000000', category: 'File Formats & Standards' },
    { key: 'xml', name: 'XML', color: '#ff6600', category: 'File Formats & Standards' },
    { key: 'yaml', name: 'YAML', color: '#cb171e', category: 'File Formats & Standards' },
    { key: 'csv', name: 'CSV', color: '#217346', category: 'File Formats & Standards' },
    { key: 'markdown', name: 'Markdown', color: '#000000', category: 'File Formats & Standards' },
    { key: 'pdf', name: 'PDF', color: '#ff0000', category: 'File Formats & Standards' },
    { key: 'svg', name: 'SVG', color: '#ffb13b', category: 'File Formats & Standards' },
    { key: 'png', name: 'PNG', color: '#0066cc', category: 'File Formats & Standards' },
    { key: 'jpg', name: 'JPEG', color: '#000080', category: 'File Formats & Standards' },
    { key: 'webp', name: 'WebP', color: '#4285f4', category: 'File Formats & Standards' }
  ],

  'Hardware & IoT': [
    { key: 'raspberry-pi', name: 'Raspberry Pi', color: '#c51a4a', category: 'Hardware & IoT' },
    { key: 'arduino', name: 'Arduino', color: '#00979d', category: 'Hardware & IoT' },
    { key: 'nvidia', name: 'NVIDIA', color: '#76b900', category: 'Hardware & IoT' },
    { key: 'intel', name: 'Intel', color: '#0071c5', category: 'Hardware & IoT' },
    { key: 'amd', name: 'AMD', color: '#ed1c24', category: 'Hardware & IoT' },
    { key: 'arm', name: 'ARM', color: '#0091bd', category: 'Hardware & IoT' },
    { key: 'iot', name: 'Internet of Things', color: '#ff6b35', category: 'Hardware & IoT' },
    { key: 'mqtt', name: 'MQTT', color: '#660066', category: 'Hardware & IoT' },
    { key: 'bluetooth', name: 'Bluetooth', color: '#0082fc', category: 'Hardware & IoT' },
    { key: 'wifi', name: 'Wi-Fi', color: '#005fcc', category: 'Hardware & IoT' }
  ],

  'Protocols & Standards': [
    { key: 'rest-api', name: 'REST API', color: '#ff6b35', category: 'Protocols & Standards' },
    { key: 'soap', name: 'SOAP', color: '#1f497d', category: 'Protocols & Standards' },
    { key: 'openapi', name: 'OpenAPI', color: '#6ba539', category: 'Protocols & Standards' },
    { key: 'json-rpc', name: 'JSON-RPC', color: '#326ce5', category: 'Protocols & Standards' },
    { key: 'oauth2', name: 'OAuth 2.0', color: '#3c4043', category: 'Protocols & Standards' },
    { key: 'saml', name: 'SAML', color: '#0066cc', category: 'Protocols & Standards' },
    { key: 'ldap', name: 'LDAP', color: '#ff6600', category: 'Protocols & Standards' },
    { key: 'cors', name: 'CORS', color: '#ff4757', category: 'Protocols & Standards' },
    { key: 'csrf', name: 'CSRF Protection', color: '#2f3542', category: 'Protocols & Standards' },
    { key: 'sso', name: 'Single Sign-On', color: '#3742fa', category: 'Protocols & Standards' }
  ],

  'Performance & Optimization': [
    { key: 'cdn', name: 'CDN', color: '#ff6b6b', category: 'Performance & Optimization' },
    { key: 'cache', name: 'Caching', color: '#4ecdc4', category: 'Performance & Optimization' },
    { key: 'compression', name: 'Compression', color: '#45b7d1', category: 'Performance & Optimization' },
    { key: 'minification', name: 'Minification', color: '#96ceb4', category: 'Performance & Optimization' },
    { key: 'lazy-loading', name: 'Lazy Loading', color: '#ffeaa7', category: 'Performance & Optimization' },
    { key: 'tree-shaking', name: 'Tree Shaking', color: '#74b9ff', category: 'Performance & Optimization' },
    { key: 'code-splitting', name: 'Code Splitting', color: '#fd79a8', category: 'Performance & Optimization' },
    { key: 'hot-reload', name: 'Hot Reload', color: '#fdcb6e', category: 'Performance & Optimization' },
    { key: 'pwa', name: 'Progressive Web App', color: '#6c5ce7', category: 'Performance & Optimization' },
    { key: 'amp', name: 'AMP', color: '#005af0', category: 'Performance & Optimization' }
  ],

  'Virtualization & Containers': [
    { key: 'virtualbox', name: 'VirtualBox', color: '#183a61', category: 'Virtualization & Containers' },
    { key: 'vmware', name: 'VMware', color: '#607078', category: 'Virtualization & Containers' },
    { key: 'vagrant', name: 'Vagrant', color: '#127eff', category: 'Virtualization & Containers' },
    { key: 'podman', name: 'Podman', color: '#892ca0', category: 'Virtualization & Containers' },
    { key: 'containerd', name: 'containerd', color: '#575757', category: 'Virtualization & Containers' },
    { key: 'crio', name: 'CRI-O', color: '#326ce5', category: 'Virtualization & Containers' },
    { key: 'lxc', name: 'LXC', color: '#333333', category: 'Virtualization & Containers' },
    { key: 'qemu', name: 'QEMU', color: '#ff6600', category: 'Virtualization & Containers' },
    { key: 'hyper-v', name: 'Hyper-V', color: '#0078d4', category: 'Virtualization & Containers' },
    { key: 'xen', name: 'Xen', color: '#0066cc', category: 'Virtualization & Containers' }
  ],

  'Additional Technologies': [
    { key: 'rabbitmq', name: 'RabbitMQ', color: '#ff6600', category: 'Additional Technologies' },
    { key: 'kafka', name: 'Apache Kafka', color: '#231f20', category: 'Additional Technologies' },
    { key: 'activemq', name: 'ActiveMQ', color: '#d30000', category: 'Additional Technologies' },
    { key: 'celery', name: 'Celery', color: '#37b24d', category: 'Additional Technologies' },
    { key: 'sidekiq', name: 'Sidekiq', color: '#b92c5c', category: 'Additional Technologies' },
    { key: 'socket-io', name: 'Socket.IO', color: '#010101', category: 'Additional Technologies' },
    { key: 'webrtc', name: 'WebRTC', color: '#333333', category: 'Additional Technologies' },
    { key: 'electron', name: 'Electron', color: '#47848f', category: 'Additional Technologies' },
    { key: 'tauri', name: 'Tauri', color: '#ffc131', category: 'Additional Technologies' },
    { key: 'pwa-tools', name: 'PWA Tools', color: '#5a0fc8', category: 'Additional Technologies' },
    { key: 'webassembly', name: 'WebAssembly', color: '#654ff0', category: 'Additional Technologies' },
    { key: 'wasm', name: 'WASM', color: '#654ff0', category: 'Additional Technologies' },
    { key: 'jamstack', name: 'JAMstack', color: '#f0047f', category: 'Additional Technologies' },
    { key: 'headless-cms', name: 'Headless CMS', color: '#6366f1', category: 'Additional Technologies' },
    { key: 'serverless', name: 'Serverless', color: '#fd5750', category: 'Additional Technologies' },
    { key: 'edge-computing', name: 'Edge Computing', color: '#0ea5e9', category: 'Additional Technologies' },
    { key: 'micro-frontend', name: 'Micro Frontend', color: '#8b5cf6', category: 'Additional Technologies' },
    { key: 'micro-services', name: 'Microservices', color: '#06b6d4', category: 'Additional Technologies' },
    { key: 'api-gateway', name: 'API Gateway', color: '#84cc16', category: 'Additional Technologies' },
    { key: 'service-mesh', name: 'Service Mesh', color: '#f59e0b', category: 'Additional Technologies' },
    { key: 'graalvm', name: 'GraalVM', color: '#ee7203', category: 'Additional Technologies' },
    { key: 'quarkus', name: 'Quarkus', color: '#4695eb', category: 'Additional Technologies' },
    { key: 'micronaut', name: 'Micronaut', color: '#0088cc', category: 'Additional Technologies' },
    { key: 'helidon', name: 'Helidon', color: '#e03c31', category: 'Additional Technologies' },
    { key: 'openshift', name: 'OpenShift', color: '#ee0000', category: 'Additional Technologies' },
    { key: 'istio', name: 'Istio', color: '#466bb0', category: 'Additional Technologies' },
    { key: 'envoy', name: 'Envoy Proxy', color: '#ac6199', category: 'Additional Technologies' },
    { key: 'consul', name: 'HashiCorp Consul', color: '#e03875', category: 'Additional Technologies' },
    { key: 'nomad', name: 'HashiCorp Nomad', color: '#1daeff', category: 'Additional Technologies' },
    { key: 'boundary', name: 'HashiCorp Boundary', color: '#1daeff', category: 'Additional Technologies' },
    { key: 'waypoint', name: 'HashiCorp Waypoint', color: '#1daeff', category: 'Additional Technologies' },
    { key: 'packer', name: 'HashiCorp Packer', color: '#1daeff', category: 'Additional Technologies' },
    { key: 'vagrant-cloud', name: 'Vagrant Cloud', color: '#127eff', category: 'Additional Technologies' },
    { key: 'circleci', name: 'CircleCI', color: '#343434', category: 'Additional Technologies' },
    { key: 'travis-ci', name: 'Travis CI', color: '#3eaaaf', category: 'Additional Technologies' },
    { key: 'github-actions', name: 'GitHub Actions', color: '#2088ff', category: 'Additional Technologies' },
    { key: 'gitlab-ci', name: 'GitLab CI', color: '#fc6d26', category: 'Additional Technologies' },
    { key: 'azure-pipelines', name: 'Azure Pipelines', color: '#2560e0', category: 'Additional Technologies' },
    { key: 'bamboo', name: 'Atlassian Bamboo', color: '#0052cc', category: 'Additional Technologies' },
    { key: 'teamcity', name: 'TeamCity', color: '#000000', category: 'Additional Technologies' },
    { key: 'drone', name: 'Drone CI', color: '#212121', category: 'Additional Technologies' },
    { key: 'buildkite', name: 'Buildkite', color: '#14cc80', category: 'Additional Technologies' },
    { key: 'codefresh', name: 'Codefresh', color: '#1976d2', category: 'Additional Technologies' },
    { key: 'spinnaker', name: 'Spinnaker', color: '#139bb4', category: 'Additional Technologies' },
    { key: 'argo-cd', name: 'Argo CD', color: '#ef7b4d', category: 'Additional Technologies' },
    { key: 'flux', name: 'Flux', color: '#5468ff', category: 'Additional Technologies' },
    { key: 'tekton', name: 'Tekton', color: '#ffd43b', category: 'Additional Technologies' },
    { key: 'concourse', name: 'Concourse CI', color: '#3398dc', category: 'Additional Technologies' },
    { key: 'woodpecker', name: 'Woodpecker CI', color: '#4caf50', category: 'Additional Technologies' }
  ],

  'Extended Tools': [
    { key: 'postman-newman', name: 'Newman (Postman CLI)', color: '#ff6c37', category: 'Extended Tools' },
    { key: 'bruno', name: 'Bruno API', color: '#f59e0b', category: 'Extended Tools' },
    { key: 'httpie', name: 'HTTPie', color: '#73dc8c', category: 'Extended Tools' },
    { key: 'curl', name: 'cURL', color: '#073551', category: 'Extended Tools' },
    { key: 'wget', name: 'Wget', color: '#0066cc', category: 'Extended Tools' },
    { key: 'jq', name: 'jq (JSON processor)', color: '#c4840f', category: 'Extended Tools' },
    { key: 'yq', name: 'yq (YAML processor)', color: '#ffd43b', category: 'Extended Tools' },
    { key: 'ripgrep', name: 'ripgrep', color: '#1e88e5', category: 'Extended Tools' },
    { key: 'fd', name: 'fd (find alternative)', color: '#f59e0b', category: 'Extended Tools' },
    { key: 'bat', name: 'bat (cat alternative)', color: '#5e81ac', category: 'Extended Tools' },
    { key: 'exa', name: 'exa (ls alternative)', color: '#a3be8c', category: 'Extended Tools' },
    { key: 'zsh', name: 'Zsh', color: '#f15a24', category: 'Extended Tools' },
    { key: 'bash', name: 'Bash', color: '#4eaa25', category: 'Extended Tools' },
    { key: 'powershell', name: 'PowerShell', color: '#012456', category: 'Extended Tools' },
    { key: 'fish', name: 'Fish Shell', color: '#000000', category: 'Extended Tools' }
  ]
};

// 🎯 MEGA MAPPING - Tous les composants disponibles
const COMPONENT_PRIORITY: Record<string, any> = {
  // Frontend Frameworks
  react: Si.SiReact,
  vue: Si.SiVuedotjs,
  angular: Si.SiAngular,
  svelte: Si.SiSvelte,
  nextjs: Si.SiNextdotjs,
  nuxtjs: Si.SiNuxtdotjs,
  gatsby: Si.SiGatsby,
  astro: Si.SiAstro,
  remix: Si.SiRemix,
  qwik: Si.SiQwik,
  solid: Si.SiSolid,
  preact: Si.SiPreact,
  ember: Si.SiEmberdotjs,
  backbone: Si.SiBackbonedotjs,
  meteor: Si.SiMeteor,
  alpine: Fa.FaMountain,
  lit: Si.SiLit,
  stencil: Si.SiStencil,
  mithril: Fa.FaCode,
  inferno: Fa.FaFire,

  // Programming Languages
  javascript: Si.SiJavascript,
  typescript: Si.SiTypescript,
  python: Si.SiPython,
  java: Fa.FaJava,
  csharp: Si.SiSharp,
  cpp: Si.SiCplusplus,
  c: Si.SiC,
  go: Si.SiGo,
  rust: Si.SiRust,
  php: Si.SiPhp,
  ruby: Si.SiRuby,
  swift: Si.SiSwift,
  kotlin: Si.SiKotlin,
  dart: Si.SiDart,
  scala: Si.SiScala,
  elixir: Si.SiElixir,
  clojure: Si.SiClojure,
  haskell: Si.SiHaskell,
  r: Si.SiR,
  matlab: Fa.FaCode, // Fallback
  perl: Si.SiPerl,
  lua: Si.SiLua,
  julia: Si.SiJulia,
  ocaml: Si.SiOcaml,
  erlang: Si.SiErlang,
  fsharp: Si.SiFsharp,
  groovy: Si.SiApachegroovy,
  crystal: Si.SiCrystal,
  nim: Si.SiNim,
  zig: Si.SiZig,

  // Styling & UI
  html5: Si.SiHtml5,
  css3: Fa.FaCss3Alt,
  sass: Si.SiSass,
  less: Si.SiLess,
  stylus: Si.SiStylus,
  tailwindcss: Si.SiTailwindcss,
  bootstrap: Si.SiBootstrap,
  bulma: Si.SiBulma,
  materialui: Si.SiMui,
  antdesign: Si.SiAntdesign,
  chakraui: Si.SiChakraui,
  styledcomponents: Si.SiStyledcomponents,
  emotion: Fa.FaHeart, // Fallback
  figma: Si.SiFigma,
  sketch: Si.SiSketch,
  framer: Si.SiFramer,
  'adobe-xd': Si.SiAdobexd,
  invision: Si.SiInvision,
  zeplin: Fa.FaCode, // Fallback
  principle: Fa.FaCode,
  foundation: Fa.FaCode, // Fallback
  'semantic-ui': Si.SiSemanticuireact,
  uikit: Si.SiUikit,
  windicss: Fa.FaWind, // Fallback
  postcss: Si.SiPostcss,

  // Backend Frameworks
  nodejs: Si.SiNodedotjs,
  deno: Si.SiDeno,
  bun: Si.SiBun,
  express: Si.SiExpress,
  fastify: Si.SiFastify,
  nestjs: Si.SiNestjs,
  koa: Si.SiKoa,
  laravel: Si.SiLaravel,
  django: Si.SiDjango,
  flask: Si.SiFlask,
  fastapi: Si.SiFastapi,
  spring: Si.SiSpring,
  dotnet: Si.SiDotnet,
  rails: Si.SiRubyonrails,
  gin: Si.SiGin,
  fiber: Fa.FaCode,
  actix: Fa.FaCode,
  rocket: Fa.FaRocket,
  phoenix: Si.SiPhoenixframework,
  sinatra: Fa.FaCode,
  tornado: Fa.FaCode, // Fallback (no FaTornado)
  aiohttp: Fa.FaCode,
  hapi: Fa.FaCode,
  sails: Si.SiSailsdotjs,
  adonis: Si.SiAdonisjs,

  // Databases
  postgresql: Si.SiPostgresql,
  mysql: Si.SiMysql,
  mongodb: Si.SiMongodb,
  redis: Si.SiRedis,
  sqlite: Si.SiSqlite,
  mariadb: Si.SiMariadb,
  supabase: Si.SiSupabase,
  firebase: Si.SiFirebase,
  cassandra: Si.SiApachecassandra,
  elasticsearch: Si.SiElasticsearch,
  neo4j: Si.SiNeo4J, // Correct case
  dynamodb: Si.SiAmazondynamodb,
  couchdb: Fa.FaDatabase, // Fallback
  influxdb: Si.SiInfluxdb,
  timescaledb: Si.SiTimescale,
  cockroachdb: Si.SiCockroachlabs,
  planetscale: Si.SiPlanetscale,
  fauna: Si.SiFauna, // Correct name
  arangodb: Si.SiArangodb,
  rethinkdb: Fa.FaDatabase, // Fallback

  // Cloud & DevOps
  docker: Si.SiDocker,
  kubernetes: Si.SiKubernetes,
  jenkins: Si.SiJenkins,
  terraform: Si.SiTerraform,
  ansible: Si.SiAnsible,
  nginx: Si.SiNginx,
  aws: Si.SiAmazon, // Correct name
  gcp: Si.SiGooglecloud,
  azure: Fa.FaCloud, // Fallback
  vercel: Si.SiVercel,
  netlify: Si.SiNetlify,
  heroku: Si.SiHeroku,
  digitalocean: Si.SiDigitalocean,
  linode: Fa.FaCloud, // Fallback
  vultr: Si.SiVultr,
  cloudflare: Si.SiCloudflare,
  railway: Si.SiRailway,
  render: Si.SiRender,
  fly: Fa.FaPlane,
  'supabase-edge': Si.SiSupabase,

  // Development Tools
  // Note: No SiVisualstudiocode export in react-icons v5; fallback to FaCode
  vscode: Fa.FaCode,
  webstorm: Si.SiWebstorm,
  intellij: Si.SiIntellijidea,
  pycharm: Si.SiPycharm,
  androidstudio: Si.SiAndroidstudio,
  xcode: Si.SiXcode,
  vim: Si.SiVim,
  neovim: Si.SiNeovim,
  emacs: Si.SiGnuemacs,
  sublime: Si.SiSublimetext,
  atom: Fa.FaAtom, // Fallback
  bracket: Fa.FaCode,
  rider: Si.SiRider,
  eclipse: Si.SiEclipseide,
  netbeans: Si.SiApachenetbeanside,
  codepen: Si.SiCodepen,
  codesandbox: Si.SiCodesandbox,
  replit: Si.SiReplit,
  gitpod: Si.SiGitpod,
  stackblitz: Si.SiStackblitz,

  // Build Tools
  webpack: Si.SiWebpack,
  vite: Si.SiVite,
  rollup: Si.SiRollupdotjs,
  parcel: Fa.FaBox, // Fallback
  babel: Si.SiBabel,
  eslint: Si.SiEslint,
  prettier: Si.SiPrettier,
  gulp: Si.SiGulp,
  grunt: Si.SiGrunt,
  turbo: Si.SiTurborepo,
  esbuild: Si.SiEsbuild,
  swc: Si.SiSwc,
  rome: Fa.FaCode,
  biome: Fa.FaLeaf,
  vitest: Si.SiVitest,

  // Version Control
  git: Si.SiGit,
  github: Si.SiGithub,
  gitlab: Si.SiGitlab,
  bitbucket: Si.SiBitbucket,
  'azure-devops': Fa.FaCode, // Fallback
  sourcetree: Si.SiSourcetree,
  gitkraken: Si.SiGitkraken,
  tower: Fa.FaCode,
  fork: Fa.FaCodeBranch,
  svn: Si.SiSubversion,

  // Mobile Development
  reactnative: Si.SiReact,
  flutter: Si.SiFlutter,
  ionic: Si.SiIonic,
  xamarin: Fa.FaMobile, // Fallback
  cordova: Si.SiApachecordova,
  phonegap: Fa.FaMobile, // Fallback
  nativescript: Si.SiNativescript,
  expo: Si.SiExpo,
  capacitor: Si.SiCapacitor,
  quasar: Si.SiQuasar,

  // Testing Frameworks
  jest: Si.SiJest,
  cypress: Si.SiCypress,
  playwright: Fa.FaTheaterMasks, // Fallback
  selenium: Si.SiSelenium,
  puppeteer: Si.SiPuppeteer,
  'testing-library': Si.SiTestinglibrary,
  mocha: Si.SiMocha,
  jasmine: Si.SiJasmine,
  karma: Fa.FaCode, // Fallback
  storybook: Si.SiStorybook,
  chromatic: Si.SiChromatic,

  // Data Science & AI
  tensorflow: Si.SiTensorflow,
  pytorch: Si.SiPytorch,
  pandas: Si.SiPandas,
  numpy: Si.SiNumpy,
  'scikit-learn': Si.SiScikitlearn,
  jupyter: Si.SiJupyter,
  anaconda: Si.SiAnaconda,
  opencv: Si.SiOpencv,
  keras: Si.SiKeras,
  plotly: Si.SiPlotly,
  tableau: Si.SiTableau,
  powerbi: Fa.FaChartBar, // Fallback
  'apache-spark': Si.SiApachespark,
  databricks: Si.SiDatabricks,
  snowflake: Si.SiSnowflake,

  // CMS & E-commerce
  wordpress: Si.SiWordpress,
  drupal: Si.SiDrupal,
  joomla: Si.SiJoomla,
  contentful: Si.SiContentful,
  strapi: Si.SiStrapi,
  sanity: Si.SiSanity,
  ghost: Si.SiGhost,
  shopify: Si.SiShopify,
  woocommerce: Si.SiWoo,
  magento: Si.SiMagento,
  bigcommerce: Si.SiBigcommerce,
  prestashop: Si.SiPrestashop,

  // Game Development
  unity: Si.SiUnity,
  unreal: Si.SiUnrealengine,
  godot: Si.SiGodotengine,
  construct: Fa.FaGamepad,
  gamemaker: Fa.FaGamepad,
  defold: Fa.FaGamepad,
  cocos2d: Fa.FaGamepad,
  love2d: Fa.FaHeart,
  phaser: Fa.FaGamepad,
  threejs: Si.SiThreedotjs,

  // Blockchain & Web3
  ethereum: Si.SiEthereum,
  bitcoin: Si.SiBitcoin,
  solana: Si.SiSolana,
  polygon: Si.SiPolygon,
  chainlink: Si.SiChainlink,
  web3js: Fa.FaEthereum, // Fallback
  ethers: Fa.FaEthereum,
  metamask: Fa.FaWallet, // Fallback
  truffle: Fa.FaCode,
  hardhat: Fa.FaHammer,

  // APIs & Integration
  graphql: Si.SiGraphql,
  rest: Fa.FaExchangeAlt,
  grpc: Fa.FaCode,
  apollo: Si.SiApollographql,
  postman: Si.SiPostman,
  insomnia: Si.SiInsomnia,
  swagger: Si.SiSwagger,
  webhook: Fa.FaCode, // Fallback
  zapier: Si.SiZapier,
  ifttt: Si.SiIfttt,

  // Security & DevSecOps
  oauth: Fa.FaKey,
  jwt: Fa.FaKey,
  auth0: Si.SiAuth0,
  okta: Si.SiOkta,
  keycloak: Si.SiKeycloak,
  vault: Si.SiVault,
  sonarqube: Si.SiSonarqube,
  snyk: Si.SiSnyk,
  owasp: Fa.FaCode, // Fallback
  letsencrypt: Si.SiLetsencrypt,

  // Monitoring & Analytics
  prometheus: Si.SiPrometheus,
  grafana: Si.SiGrafana,
  datadog: Si.SiDatadog,
  newrelic: Si.SiNewrelic,
  sentry: Si.SiSentry,
  bugsnag: Si.SiBugsnag,
  rollbar: Fa.FaCode, // Fallback
  hotjar: Si.SiHotjar,
  googleanalytics: Si.SiGoogleanalytics,
  mixpanel: Si.SiMixpanel,

  // Operating Systems
  windows: Fa.FaWindows,
  macos: Si.SiMacos,
  linux: Si.SiLinux,
  ubuntu: Si.SiUbuntu,
  debian: Si.SiDebian,
  centos: Si.SiCentos,
  redhat: Si.SiRedhat,
  fedora: Si.SiFedora,
  arch: Si.SiArchlinux,
  opensuse: Si.SiOpensuse,
  freebsd: Si.SiFreebsd,
  'alpine-linux': Si.SiAlpinelinux,

  // Networking & Protocols
  http: Fa.FaGlobe,
  https: Fa.FaLock,
  tcp: Fa.FaNetworkWired,
  websocket: Fa.FaPlug,
  ssh: Fa.FaTerminal,
  ftp: Fa.FaFolder,
  smtp: Fa.FaEnvelope,
  dns: Fa.FaServer,
  vpn: Fa.FaKey,
  proxy: Fa.FaExchangeAlt,

  // File Formats & Standards
  json: Si.SiJson,
  xml: Fa.FaCode,
  yaml: Fa.FaCode,
  csv: Fa.FaTable,
  markdown: Si.SiMarkdown,
  pdf: Fa.FaFilePdf,
  svg: Fa.FaImage,
  png: Fa.FaImage,
  jpg: Fa.FaImage,
  webp: Fa.FaImage,

  // Hardware & IoT
  'raspberry-pi': Si.SiRaspberrypi,
  arduino: Si.SiArduino,
  nvidia: Si.SiNvidia,
  intel: Si.SiIntel,
  amd: Si.SiAmd,
  arm: Fa.FaCode,
  iot: Fa.FaWifi,
  mqtt: Fa.FaCode,
  bluetooth: Si.SiBluetooth,
  wifi: Fa.FaWifi,

  // Protocols & Standards
  'rest-api': Fa.FaExchangeAlt,
  soap: Fa.FaCode,
  openapi: Si.SiSwagger,
  'json-rpc': Fa.FaCode,
  oauth2: Fa.FaKey,
  saml: Fa.FaKey,
  ldap: Fa.FaUsers,
  cors: Fa.FaGlobe,
  csrf: Fa.FaShieldAlt,
  sso: Fa.FaKey,

  // Performance & Optimization
  cdn: Fa.FaCloud,
  cache: Fa.FaDatabase,
  compression: Fa.FaCompress,
  minification: Fa.FaCompressAlt,
  'lazy-loading': Fa.FaSpinner,
  'tree-shaking': Fa.FaTree,
  'code-splitting': Fa.FaCodeBranch,
  'hot-reload': Fa.FaSync,
  pwa: Fa.FaMobile,
  amp: Fa.FaBolt,

  // Virtualization & Containers
  virtualbox: Si.SiVirtualbox,
  vmware: Si.SiVmware,
  vagrant: Si.SiVagrant,
  podman: Fa.FaDocker,
  containerd: Fa.FaDocker,
  crio: Fa.FaDocker,
  lxc: Fa.FaDocker,
  qemu: Fa.FaDesktop,
  'hyper-v': Fa.FaDesktop,
  xen: Fa.FaDesktop,

  // Additional Technologies
  rabbitmq: Si.SiRabbitmq,
  kafka: Si.SiApachekafka,
  activemq: Fa.FaCode,
  celery: Fa.FaCode,
  sidekiq: Fa.FaCode,
  'socket-io': Si.SiSocketdotio,
  webrtc: Fa.FaVideo,
  electron: Si.SiElectron,
  tauri: Si.SiTauri,
  'pwa-tools': Fa.FaMobile,
  webassembly: Si.SiWebassembly,
  wasm: Si.SiWebassembly,
  jamstack: Fa.FaLayerGroup,
  'headless-cms': Fa.FaCog,
  serverless: Fa.FaCloud,
  'edge-computing': Fa.FaGlobe,
  'micro-frontend': Fa.FaPuzzlePiece,
  'micro-services': Fa.FaCubes,
  'api-gateway': Fa.FaDoorOpen,
  'service-mesh': Fa.FaProjectDiagram,
  graalvm: Fa.FaCode,
  quarkus: Si.SiQuarkus,
  micronaut: Fa.FaCode,
  helidon: Fa.FaCode,
  openshift: Si.SiRedhatopenshift,
  istio: Fa.FaCode,
  envoy: Fa.FaCode,
  consul: Si.SiConsul,
  nomad: Fa.FaCode,
  boundary: Fa.FaCode,
  waypoint: Fa.FaCode,
  packer: Si.SiPacker,
  'vagrant-cloud': Si.SiVagrant,
  circleci: Si.SiCircleci,
  'travis-ci': Si.SiTravisci,
  'github-actions': Si.SiGithubactions,
  'gitlab-ci': Si.SiGitlab,
  'azure-pipelines': Fa.FaCode,
  bamboo: Si.SiAtlassian,
  teamcity: Si.SiJetbrains,
  drone: Si.SiDrone,
  buildkite: Si.SiBuildkite,
  codefresh: Fa.FaCode,
  spinnaker: Fa.FaCode,
  'argo-cd': Fa.FaCode,
  flux: Fa.FaCode,
  tekton: Fa.FaCode,
  concourse: Fa.FaCode,
  woodpecker: Fa.FaCode,

  // Extended Tools
  'postman-newman': Si.SiPostman,
  bruno: Fa.FaCode,
  httpie: Fa.FaCode,
  curl: Fa.FaTerminal,
  wget: Fa.FaDownload,
  jq: Fa.FaCode,
  yq: Fa.FaCode,
  ripgrep: Fa.FaSearch,
  fd: Fa.FaSearch,
  bat: Fa.FaCat,
  exa: Fa.FaList,
  zsh: Fa.FaTerminal,
  bash: Fa.FaTerminal,
  powershell: Fa.FaTerminal,
  fish: Fa.FaTerminal,

  // Generic fallbacks
  code: Fa.FaCode,
  web: Md.MdWeb,
  cloud: Fa.FaCloud,
  database: Fa.FaDatabase,
  server: Fa.FaServer,
  mobile: Fa.FaMobile,
  desktop: Fa.FaDesktop,
  api: Fa.FaPlug,
  security: Fa.FaCode, // Fallback
  analytics: Fa.FaChartBar,
  // Convenience keys used by DB
  responsive: Md.MdWeb,
  agile: Fa.FaProjectDiagram,
  // Generic aliases used by Admin UI
  tools: Fa.FaTools,
  'paint-brush': Fa.FaPaintBrush,
  'chart-bar': Fa.FaChartBar
};

// Cache pour optimiser les performances
const RESOLUTION_CACHE: Record<string, ResolvedIcon> = {};

export const getAllCategories = (): string[] => Object.keys(RAW_CATEGORIES);

export const getIconsByCategory = (category: string): IconMeta[] => RAW_CATEGORIES[category] || [];

export const resolveIcon = (key: string): ResolvedIcon => {
  const normalize = (v: string) => v
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, '');
  const k = normalize(key);
  if (RESOLUTION_CACHE[k]) return RESOLUTION_CACHE[k];

  const flatten = Object.values(RAW_CATEGORIES).flat();
  const meta = flatten.find(i => normalize(i.key) === k);
  const IconComponent = COMPONENT_PRIORITY[k] || COMPONENT_PRIORITY[meta?.key || ''];
  
  const resolved: ResolvedIcon = meta ? {
    ...meta,
    IconComponent: IconComponent || Fa.FaCode,
    hasComponent: !!IconComponent
  } : {
    key: k,
    name: k,
    color: '#6b7280',
    category: 'Custom',
    IconComponent: IconComponent || Fa.FaCode,
    hasComponent: !!IconComponent
  };
  
  RESOLUTION_CACHE[k] = resolved;
  return resolved;
};

export const listMissingComponents = (): string[] => {
  return Object.values(RAW_CATEGORIES)
    .flat()
    .filter(m => !COMPONENT_PRIORITY[m.key])
    .map(m => m.key)
    .sort();
};

export const getTotalIconCount = (): number => {
  return Object.values(RAW_CATEGORIES).flat().length;
};

export const ICON_LIBRARY = RAW_CATEGORIES;

export default {
  getAllCategories,
  getIconsByCategory,
  resolveIcon,
  listMissingComponents,
  getTotalIconCount,
  ICON_LIBRARY
};
